'use client';

import { Box, Card, CircularProgress, Tooltip } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { green, slate } from '@/lib/colours';
import { useResolvedColorScheme } from '@/hooks/useResolvedColorScheme';
import Link from 'next/link';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { useDocumentImageWithSociety } from '@/hooks/publicSearch/useDocumentImageWithSociety';
import { findLogoDocument } from '@/publicSearch/utils/media';
import { Cell, PropsHeader, PropsValue } from './cardStyles';

const StyledCard = styled(Card)(({ theme }) => ({
  padding: '16px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: `0 0 8px ${alpha(theme.palette.common.black, 0.1)}`,
  border: `1px solid ${(theme.vars ?? theme).palette.divider}`,
  [theme.breakpoints.down('sm')]: {
    padding: '12px 16px',
    borderRadius: 0,
    boxShadow: 'none',
    border: 'none',
    borderBottom: `1px solid ${(theme.vars ?? theme).palette.divider}`,
  },
}));

const Name = styled(Link)(({ theme }) => ({
  display: '-webkit-box',
  lineClamp: 2,
  overflow: 'hidden',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  wordBreak: 'break-word',
  textOverflow: 'ellipsis',
  color: (theme.vars ?? theme).palette.info.main,
  fontSize: '18px',
  fontWeight: 500,
  textDecoration: 'none',
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
  },
}));

const FieldGrid = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  columnGap: '16px',
  rowGap: '4px',
});

const BadgeStatus = (props: { status: string }) => {
  const { status } = props;
  const { isDarkMode } = useResolvedColorScheme();
  if (!status) return null;
  const isActive = status === 'Active';
  let background: string;
  let color: string;
  if (isActive) {
    background = isDarkMode ? alpha(green[500], 0.2) : green[100];
    color = isDarkMode ? green[300] : green[700];
  } else {
    background = isDarkMode ? alpha(slate[400], 0.2) : slate[100];
    color = isDarkMode ? slate[300] : slate[700];
  }
  return (
    <Box
      component="span"
      sx={{
        borderRadius: '4px',
        p: '4px 12px',
        width: 'fit-content',
        fontSize: '12px',
        lineHeight: '16px',
        textTransform: 'capitalize',
        background,
        color,
      }}
    >
      {status}
    </Box>
  );
};

export type MemberCardProps = {
  data: {
    name: string;
    status: string;
    herdPrefix: string;
    membershipType: string;
    memberIdent: string;
    herdLetters: string;
    postcode: string;
    memberId?: number | string;
    detailPath?: string;
    documents?: Array<{ fileName?: string | null; url?: string | null }>;
  };
};

export default function MemberCard(props: MemberCardProps) {
  const { data } = props;
  const detailPath = data.detailPath;
  const society = usePublicSearchContext((c) => c.society);
  const societyId = society.societyId ?? (society as any).orgId ?? '';
  const primaryImg = findLogoDocument(data.documents)?.url ?? null;
  const { imgSrc: displayImg } = useDocumentImageWithSociety(
    primaryImg,
    societyId,
  );
  const fields = [
    { label: 'Stud No.', value: data.memberIdent },
    { label: 'Herd Letters', value: data.herdLetters },
    { label: 'Postcode', value: data.postcode },
    { label: 'Stud Prefix', value: data.herdPrefix },
    { label: 'Membership Type', value: data.membershipType },
  ].filter(({ value }) => String(value ?? '').trim());

  return (
    <StyledCard>
      <Box
        sx={{
          mb: '12px',
        }}
      >
        <Box
          sx={{
            height: { sm: '24px' },
            mb: '4px',
          }}
        >
          {data.status && <BadgeStatus status={data.status} />}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {detailPath && data.name && (
              <Tooltip disableInteractive title="View Details">
                <Name href={detailPath}>{data.name}</Name>
              </Tooltip>
            )}
          </Box>
          {displayImg && (
            <Box
              component="img"
              src={displayImg}
              alt=""
              sx={{
                width: '25%',
                maxWidth: '80px',
                margin: 1,
                borderRadius: 1,
                alignSelf: 'center',
                border: '2px solid #fff',
              }}
            />
          )}
          {!displayImg && primaryImg && (
            <Box
              sx={{
                width: '25%',
                maxWidth: '80px',
                aspectRatio: '4/3',
                margin: 1,
                alignSelf: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'background.default',
                borderRadius: 1,
                padding: 1,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>
      </Box>
      {fields.length > 0 && (
        <FieldGrid>
          {fields.map((field) => (
            <Cell key={field.label}>
              <PropsHeader>{field.label}</PropsHeader>
              <PropsValue>{field.value}</PropsValue>
            </Cell>
          ))}
        </FieldGrid>
      )}
    </StyledCard>
  );
}
